import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { TablePagination } from "@/components/TablePagination";
import { Plus, Download, Check, X, FileText, Search } from "lucide-react";
import { showConfirm, showError, showSuccess } from "@/utils/dialog";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [refundFilter, setRefundFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [approvalPolicy, setApprovalPolicy] = useState("any_one");
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("invoices.visibleColumns");
      return saved
        ? JSON.parse(saved)
        : {
            invoiceNumber: true,
            type: true,
            customer: true,
            amount: true,
            status: true,
            payment: true,
            managerApproval: true,
            accountantApproval: true,
            date: true,
          };
    } catch {
      return {
        invoiceNumber: true,
        type: true,
        customer: true,
        amount: true,
        status: true,
        payment: true,
        managerApproval: true,
        accountantApproval: true,
        date: true,
      };
    }
  });
  const { user } = useAuth();
  const canApproveReject = user?.role === "finance" || user?.role === "admin";
  const canCreateInvoices = user?.role === "sales" || user?.role === "admin";

  const filtered = useMemo(() => {
    let list = [...(invoices || [])];
    const s = (search || "").toLowerCase().trim();
    if (s) {
      list = list.filter(
        (inv) =>
          (inv.invoice_number || "").toLowerCase().includes(s) ||
          (inv.customer_name || "").toLowerCase().includes(s),
      );
    }
    if (statusFilter) {
      list = list.filter((inv) => (inv.status || "").toLowerCase() === statusFilter.toLowerCase());
    }
    if (refundFilter === "refunded") {
      list = list.filter((inv) => {
        const paymentStatus = String(
          inv.display_payment_status || inv.payment_status || "",
        ).toLowerCase();
        const refundedTotal = parseFloat(inv.refunded_total || 0);
        return (
          refundedTotal > 0 ||
          paymentStatus === "refunded" ||
          paymentStatus === "partially_refunded"
        );
      });
    }
    return list;
  }, [invoices, search, statusFilter, refundFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const columnCount = 1 + Object.values(visibleColumns).filter(Boolean).length;

  useEffect(() => {
    localStorage.setItem("invoices.visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, refundFilter, pageSize]);

  useEffect(() => {
    fetchInvoices();
    api.settings
      .getCompany()
      .then((s) =>
        setApprovalPolicy(
          ["none", "any_one", "both"].includes(s?.invoice_approval_policy)
            ? s.invoice_approval_policy
            : "any_one",
        ),
      )
      .catch(() => {});
  }, []);

  const fetchInvoices = async () => {
    try {
      const data = await api.invoices.getAll();
      setInvoices(data);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-emerald-100 text-emerald-700",
      pending_approval: "bg-amber-100 text-amber-700",
      rejected: "bg-red-100 text-red-700",
      draft: "bg-stone-100 text-stone-700",
    };
    return (
      <span
        className={`rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${
          styles[status] || styles.draft
        }`}
      >
        {status.replace("_", " ")}
      </span>
    );
  };

  const getApprovalCellText = (invoice, role) => {
    if (approvalPolicy === "none") return "Not required";
    const managerApproved = Boolean(invoice?.manager_approved);
    const accountantApproved = Boolean(invoice?.accountant_approved);
    if (approvalPolicy === "both") {
      return role === "manager"
        ? managerApproved
          ? "Approved"
          : "Pending"
        : accountantApproved
          ? "Approved"
          : "Pending";
    }
    // any_one policy
    if (role === "manager") {
      if (managerApproved) return "Approved";
      if (accountantApproved) return "Not required (Finance approved)";
      return "Pending";
    }
    if (accountantApproved) return "Approved";
    if (managerApproved) return "Not required (Manager approved)";
    return "Pending";
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.invoices.exportPDF(invoiceId);

      // Create blob from response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
      await showError("Failed to download invoice PDF. Please try again.");
    }
  };

  const handleApprove = async (invoiceId, invoiceNumber) => {
    const confirmed = await showConfirm(
      `Are you sure you want to approve invoice ${invoiceNumber}?`,
      "Approve Invoice",
      "Approve",
    );
    if (!confirmed) {
      return;
    }

    try {
      await api.invoices.approve(invoiceId);
      await showSuccess("Invoice approved successfully!");
      fetchInvoices(); // Refresh the list
    } catch (error) {
      console.error("Failed to approve invoice:", error);
      await showError(error.message || "Failed to approve invoice. Please try again.");
    }
  };

  const handleReject = async (invoiceId, invoiceNumber) => {
    const confirmed = await showConfirm(
      `Are you sure you want to reject invoice ${invoiceNumber}?`,
      "Reject Invoice",
      "Reject",
    );
    if (!confirmed) {
      return;
    }

    try {
      await api.invoices.reject(invoiceId);
      await showSuccess("Invoice rejected.");
      fetchInvoices(); // Refresh the list
    } catch (error) {
      console.error("Failed to reject invoice:", error);
      await showError(error.message || "Failed to reject invoice. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div className="min-w-0 max-w-full">
          <h1 className="text-2xl font-heading font-semibold text-secondary">Invoices</h1>
          <p className="form-helper mt-1 max-w-full">
            {canCreateInvoices
              ? <> <strong>Order invoice</strong> = created when you close an order (Orders → open order → &quot;Close &amp; Create Invoice&quot;). <strong>Temporary invoice</strong> = quick/small bill created below, not linked to an order. </>
              : "View and manage company invoices."}
          </p>
        </div>
        {canCreateInvoices && (
          <div className="flex items-center gap-2">
            <Link
              to="/orders"
              className="bg-primary hover:bg-primary/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Go to Orders
            </Link>
            <Link
              to="/invoices/new"
              className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 text-stone-700 text-sm font-medium"
              title="Create a quick bill without an order"
            >
              Temporary invoice
            </Link>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search by invoice # or customer..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => setStatusFilter("")} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${!statusFilter ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>All</button>
            <button type="button" onClick={() => setStatusFilter("pending_approval")} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "pending_approval" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>Pending</button>
            <button type="button" onClick={() => setStatusFilter("approved")} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "approved" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>Approved</button>
            <button type="button" onClick={() => setStatusFilter("rejected")} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "rejected" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>Rejected</button>
            <button type="button" onClick={() => setStatusFilter("draft")} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "draft" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>Draft</button>
            <button
              type="button"
              onClick={() => setRefundFilter(refundFilter === "refunded" ? "" : "refunded")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                refundFilter === "refunded"
                  ? "bg-purple-600 text-white"
                  : "bg-stone-100 text-stone-700"
              }`}
            >
              Refunded
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-stone-600">
          {[
            ["invoiceNumber", "Invoice #"],
            ["type", "Type"],
            ["customer", "Customer"],
            ["amount", "Amount"],
            ["status", "Status"],
            ["payment", "Payment"],
            ["managerApproval", "Manager"],
            ["accountantApproval", "Accountant"],
            ["date", "Date"],
          ].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={visibleColumns[key]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [key]: e.target.checked,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>
        <p className="form-helper mt-2">Showing {filtered.length} of {invoices.length} invoice(s)</p>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {visibleColumns.invoiceNumber && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Invoice #
                </th>}
                {visibleColumns.type && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Type
                </th>}
                {visibleColumns.customer && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Customer
                </th>}
                {visibleColumns.amount && <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Amount
                </th>}
                {visibleColumns.status && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Status
                </th>}
                {visibleColumns.payment && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Payment
                </th>}
                {visibleColumns.managerApproval && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Manager
                </th>}
                {visibleColumns.accountantApproval && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Accountant
                </th>}
                {visibleColumns.date && <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Date
                </th>}
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-6 py-8 text-center text-stone-500">
                    {invoices.length === 0 ? "No invoices found" : "No invoices match your search or filter."}
                  </td>
                </tr>
              ) : (
                paginated.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="hover:bg-stone-50 transition-colors"
                  >
                    {visibleColumns.invoiceNumber && <td className="px-4 py-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="font-mono text-sm font-medium text-secondary hover:text-primary truncate"
                        >
                          {invoice.invoice_number}
                        </Link>
                        {invoice.parent_invoice_id && (
                          <>
                            <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-cyan-100 text-cyan-700 shrink-0">
                              Add-on
                            </span>
                            <Link
                              to={`/invoices/${invoice.parent_invoice_id}`}
                              className="text-xs text-stone-500 hover:text-primary shrink-0"
                            >
                              Parent
                            </Link>
                          </>
                        )}
                      </div>
                    </td>}
                    {visibleColumns.type && <td className="px-4 py-4">
                      {invoice.order_id ? (
                        <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700" title="Created from a closed order">
                          Order
                        </span>
                      ) : (
                        <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700" title="Quick bill, not linked to an order">
                          Temporary
                        </span>
                      )}
                    </td>}
                    {visibleColumns.customer && <td className="px-4 py-4 text-sm text-stone-700 truncate" title={invoice.customer_name}>
                      {invoice.customer_name}
                    </td>}
                    {visibleColumns.amount && <td className="px-4 py-4 text-right font-mono text-sm font-semibold text-secondary">
                      {invoice.currency} {parseFloat(invoice.total).toFixed(2)}
                    </td>}
                    {visibleColumns.status && <td className="px-4 py-4">
                      {getStatusBadge(invoice.status)}
                    </td>}
                    {visibleColumns.payment && <td className="px-4 py-4">
                      {(() => {
                        const paymentStatus =
                          invoice.display_payment_status || invoice.payment_status;
                        const paymentStatusClass =
                          paymentStatus === "paid"
                            ? "bg-emerald-100 text-emerald-700"
                            : paymentStatus === "partial" ||
                                paymentStatus === "partially_refunded"
                              ? "bg-amber-100 text-amber-700"
                              : paymentStatus === "refunded"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-red-100 text-red-700";
                        return (
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${paymentStatusClass}`}
                          >
                            {String(paymentStatus || "pending").replace(/_/g, " ")}
                          </span>
                        );
                      })()}
                    </td>}
                    {visibleColumns.managerApproval && (
                      <td className="px-4 py-4 text-sm">
                        {getApprovalCellText(invoice, "manager")}
                      </td>
                    )}
                    {visibleColumns.accountantApproval && (
                      <td className="px-4 py-4 text-sm">
                        {getApprovalCellText(invoice, "accountant")}
                      </td>
                    )}
                    {visibleColumns.date && <td className="px-4 py-4 text-sm text-stone-600">
                      {new Date(invoice.created_at).toLocaleDateString()}
                    </td>}
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="View Invoice"
                        >
                          <FileText size={14} />
                          View
                        </Link>
                        {canApproveReject &&
                          approvalPolicy !== "none" &&
                          invoice.status === "pending_approval" && (
                            <>
                              <button
                                onClick={() =>
                                  handleApprove(
                                    invoice.id,
                                    invoice.invoice_number
                                  )
                                }
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Approve Invoice"
                              >
                                <Check size={14} />
                                Approve
                              </button>
                              <button
                                onClick={() =>
                                  handleReject(
                                    invoice.id,
                                    invoice.invoice_number
                                  )
                                }
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Reject Invoice"
                              >
                                <X size={14} />
                                Reject
                              </button>
                            </>
                          )}
                        <button
                          onClick={() =>
                            handleDownloadPDF(
                              invoice.id,
                              invoice.invoice_number
                            )
                          }
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-lg transition-colors"
                          title="Download PDF"
                        >
                          <Download size={14} />
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={filtered.length}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
